import {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from 'react';

interface TabDefinition {
  id: string;
  label: string;
  badge?: number | null;
  visible?: boolean;
}

interface NPSDTabBarProps {
  tabs: TabDefinition[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
}

export function NPSDTabBar({ tabs, activeTab, onTabChange }: NPSDTabBarProps) {
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [visibleTabIds, setVisibleTabIds] = useState<string[]>([]);
  const isCalculatingOverflow = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const tabWidthCacheRef = useRef<Map<string, number>>(new Map());

  // Only tabs that are visible (visible prop is true or undefined)
  const availableTabIds = useMemo(() => {
    return tabs
      .filter((tab) => tab.visible !== false)
      .map((tab) => tab.id);
  }, [tabs]);

  // Tabs that should appear in the More menu
  const overflowTabIds = useMemo(() => {
    return availableTabIds.filter((id) => !visibleTabIds.includes(id));
  }, [availableTabIds, visibleTabIds]);

  const hasOverflowTabs = useMemo(() => {
    return overflowTabIds.length > 0;
  }, [overflowTabIds]);

  const getTabById = useCallback((tabId: string): TabDefinition | undefined => {
    return tabs.find((t) => t.id === tabId);
  }, [tabs]);

  const isTabVisible = useCallback((tabId: string): boolean => {
    return visibleTabIds.includes(tabId);
  }, [visibleTabIds]);

  const isTabInOverflow = useCallback((tabId: string): boolean => {
    return overflowTabIds.includes(tabId);
  }, [overflowTabIds]);

  const calculateVisibleTabs = useCallback(() => {
    if (isCalculatingOverflow.current) return;
    isCalculatingOverflow.current = true;

    const el = containerRef.current;
    if (!el) {
      isCalculatingOverflow.current = false;
      return;
    }

    const container = el.querySelector('.npsd-tabs-container') as HTMLElement;
    const tabsList = el.querySelector('.npsd-tabs-list') as HTMLElement;

    if (!container || !tabsList) {
      isCalculatingOverflow.current = false;
      return;
    }

    const tabElements = Array.from(tabsList.querySelectorAll('.npsd-tab')) as HTMLElement[];
    const gap = 4;
    const buffer = 5;
    const moreBtnWidth = 80; // fixed estimate for "More" button

    // Measure visible tabs and cache their widths; hidden tabs keep cached value
    for (const tab of tabElements) {
      const tabId = tab.getAttribute('data-tab-id');
      if (!tabId) continue;
      const w = tab.offsetWidth;
      if (w > 0) {
        tabWidthCacheRef.current.set(tabId, w);
      }
    }

    // Calculate total width of all tabs using cached widths
    const containerWidth = container.clientWidth;
    let totalWidth = 0;
    let idx = 0;
    for (const tabId of availableTabIds) {
      const w = tabWidthCacheRef.current.get(tabId) || 100; // fallback estimate
      totalWidth += w + (idx > 0 ? gap : 0);
      idx++;
    }

    if (totalWidth <= containerWidth + buffer) {
      // All tabs fit
      setVisibleTabIds([...availableTabIds]);
      isCalculatingOverflow.current = false;
      return;
    }

    // Not all fit — calculate which ones do, reserving space for "More" button
    const availableWidth = containerWidth - moreBtnWidth;
    totalWidth = 0;
    const newVisibleIds: string[] = [];

    for (const tabId of availableTabIds) {
      const w = tabWidthCacheRef.current.get(tabId) || 100;
      const tabWidth = w + (newVisibleIds.length > 0 ? gap : 0);
      if (totalWidth + tabWidth <= availableWidth) {
        totalWidth += tabWidth;
        newVisibleIds.push(tabId);
      } else {
        break;
      }
    }

    // Always show at least 2 tabs
    if (newVisibleIds.length < 2 && availableTabIds.length >= 2) {
      newVisibleIds.length = 0;
      newVisibleIds.push(availableTabIds[0], availableTabIds[1]);
    }

    setVisibleTabIds(newVisibleIds);
    isCalculatingOverflow.current = false;
  }, [availableTabIds]);

  // Close more menu on outside click
  useEffect(() => {
    const handleDocumentClick = (e: Event) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.npsd-tabs-more')) {
        setShowMoreMenu(false);
      }
    };
    document.addEventListener('click', handleDocumentClick);
    return () => document.removeEventListener('click', handleDocumentClick);
  }, []);

  // Resize listener with rAF throttling
  useEffect(() => {
    let rafId: number | null = null;
    const throttledResize = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        calculateVisibleTabs();
      });
    };
    window.addEventListener('resize', throttledResize);
    return () => {
      window.removeEventListener('resize', throttledResize);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [calculateVisibleTabs]);

  // Recalculate when tabs change (e.g., permissions loaded)
  useEffect(() => {
    // Show all tabs initially so they can be measured, then calculate overflow
    setVisibleTabIds([...availableTabIds]);
    tabWidthCacheRef.current.clear();
    // Give DOM time to render all tabs before measuring
    const timer = setTimeout(() => calculateVisibleTabs(), 100);
    return () => clearTimeout(timer);
  }, [tabs, calculateVisibleTabs, availableTabIds]);

  const toggleMoreMenu = useCallback(() => {
    setShowMoreMenu((prev) => !prev);
  }, []);

  const selectTab = useCallback((tabId: string) => {
    onTabChange(tabId);
    setShowMoreMenu(false);
  }, [onTabChange]);

  return (
    <div className="npsd-tabs" ref={containerRef}>
      <div className="npsd-tabs-container">
        <ul className="npsd-tabs-list" role="tablist">
          {tabs.map((tab) => {
            if (tab.visible === false) return null;
            return (
              <li
                key={tab.id}
                role="presentation"
                className={`npsd-tab${activeTab === tab.id ? ' active' : ''}`}
                data-tab-id={tab.id}
                style={{ display: isTabVisible(tab.id) ? undefined : 'none' }}
              >
                <a href="#" role="tab" onClick={(e) => { e.preventDefault(); selectTab(tab.id); }}>
                  {tab.label}
                  {tab.badge != null && <span className="badge">{tab.badge}</span>}
                </a>
              </li>
            );
          })}
        </ul>
        <div
          className="npsd-tabs-more"
          style={{ display: hasOverflowTabs ? undefined : 'none' }}
        >
          <button type="button" className="npsd-tabs-more-btn" onClick={toggleMoreMenu}>
            More <i className="fa fa-chevron-down"></i>
          </button>
          <ul
            className="npsd-tabs-more-menu"
            role="menu"
            style={{ display: showMoreMenu ? undefined : 'none' }}
          >
            {tabs.map((tab) => {
              if (tab.visible === false) return null;
              return (
                <li
                  key={'more-' + tab.id}
                  style={{ display: isTabInOverflow(tab.id) ? undefined : 'none' }}
                >
                  <a href="#" onClick={(e) => { e.preventDefault(); selectTab(tab.id); }}>
                    {tab.label}
                    {tab.badge != null && <span className="badge">{tab.badge}</span>}
                  </a>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}

(window as any).NPSDTabBarReact = NPSDTabBar;
